// MCP Tools Module - Nástroje pro správu ACM
// Konzistentní struktura: Dashboard s kartami + Tool runner

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { mcpCall } from "../lib/mcp";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  PageHeader,
  Select,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "../components/ui";
import {
  Activity,
  Box,
  Braces,
  Camera,
  ChevronDown,
  ChevronRight,
  Cpu,
  Database,
  FileArchive,
  FileText,
  Globe,
  LayoutDashboard,
  List,
  Play,
  ScanText,
  Server,
  Settings,
  ShieldCheck,
  Terminal,
  Users,
  Wifi,
  Wrench,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import type { McpTool } from "../types";

// ── Icons mapping ───────────────────────────────────────────────────────

const GROUP_ICONS: Record<
  string,
  React.ComponentType<{ size?: number | string; className?: string }>
> = {
  docker: Box,
  polydoc: FileText,
  backup: FileArchive,
  snapshot: Camera,
  supervisor: Cpu,
  acm: Settings,
  npm: Globe,
  log: List,
  port: Server,
  fleet: Wifi,
  supabase: Database,
  eduroam: Wifi,
  ocr: ScanText,
  default: Terminal,
};

// ── Types ───────────────────────────────────────────────────────────────

interface SchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
}

interface Schema {
  properties?: Record<string, SchemaProperty>;
  required?: string[];
}

// ── Dashboard Komponenta ────────────────────────────────────────────────

interface AcmModule {
  name: string;
  version: string;
  description: string;
  tools: string[];
}

interface CatalogTool extends McpTool {
  scope?: string | null;
  outputSchema?: Record<string, unknown> | null;
  resource_qualified?: boolean;
}

interface RuntimeToolCatalog {
  ok: boolean;
  source: string;
  module_tools: CatalogTool[];
  project_tools: CatalogTool[];
  registered_consumers: RegisteredConsumer[];
  recent_tool_consumers: RecentToolConsumer[];
}

interface RegisteredConsumer {
  entity_id: string;
  entity_type: string;
  entity_label: string;
  credential_id: string;
  credential_label: string;
  key_preview: string | null;
  proxy: "tool_acl" | "llm";
  scopes?: string[];
  allowed_models?: string[];
  allowed_proxies?: string[] | null;
  identity_class?: string | null;
  purpose?: string | null;
  project_ref?: string | null;
}

interface RecentToolConsumer {
  tool: string;
  entity_id: string | null;
  entity_type: string | null;
  entity_label: string | null;
  calls: number;
  last_call_at: string;
}

function ToolsDashboard(
  { tools, modules }: { tools: McpTool[]; modules: AcmModule[] },
) {
  const { t } = useTranslation();
  // Skupiny podle dat z /api/modules — dynamicky, bez hardcodovaného seznamu
  const toolsByModule = tools.reduce((acc, tool) => {
    const mod = (tool as McpTool & { module?: string }).module ??
      tool.name.split("_")[0];
    if (!acc[mod]) acc[mod] = [];
    acc[mod].push(tool);
    return acc;
  }, {} as Record<string, McpTool[]>);

  // Moduly seřazené podle pořadí z API
  const moduleList = modules.length
    ? modules
    : Object.keys(toolsByModule).map((name) => ({
      name,
      version: "",
      description: "",
      tools: [],
    }));

  // Zbylé (tools bez modulu v API)
  const known = new Set(modules.map((m) => m.name));
  const other = Object.entries(toolsByModule).filter(([k]) => !known.has(k));

  return (
    <div className="space-y-6">
      <StatsGrid columns={3}>
        <StatCard
          icon={Wrench}
          label={t("tools.totalTools", "Celkem nástrojů")}
          value={tools.length}
          sub={t("tools.mcpToolsSub", "MCP tools")}
          variant="info"
        />
        <StatCard
          icon={Box}
          label={t("tools.modulesCount", "Modulů")}
          value={moduleList.length}
          sub={t("tools.loadedSub", "načteno")}
          variant="info"
        />
        <StatCard
          icon={Zap}
          label={t("tools.othersCount", "Ostatních")}
          value={other.reduce((s, [, mt]) => s + mt.length, 0)}
          sub={t("tools.toolsOutsideModulesSub", "tools mimo moduly")}
          variant="info"
        />
      </StatsGrid>

      {/* Moduly z API */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {moduleList.map((mod) => {
          const Icon = GROUP_ICONS[mod.name] || GROUP_ICONS.default;
          const modTools = toolsByModule[mod.name] || [];
          return (
            <Card key={mod.name} variant="accent">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon size={16} className="text-blue-400" />
                  <CardTitle>{mod.name}</CardTitle>
                  {mod.version && (
                    <span className="text-[10px] text-gray-600 font-mono ml-auto">
                      v{mod.version}
                    </span>
                  )}
                </div>
                {mod.description && (
                  <CardDescription>{mod.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">
                  {modTools.length}
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {modTools.slice(0, 4).map((mt) =>
                    mt.name.replace(`${mod.name}_`, "")
                  ).join(", ")}
                  {modTools.length > 4 && ` +${modTools.length - 4}`}
                </p>
              </CardContent>
            </Card>
          );
        })}
        {other.map(([name, groupTools]) => {
          const Icon = GROUP_ICONS[name] || GROUP_ICONS.default;
          return (
            <Card key={name} variant="accent">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Icon size={16} className="text-gray-400" />
                  <CardTitle>{name}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-white">
                  {groupTools.length}
                </p>
                <p className="text-xs text-gray-500 mt-1 truncate">
                  {groupTools.slice(0, 4).map((mt) => mt.name).join(", ")}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Tool Runner Tab ─────────────────────────────────────────────────────

interface SchemaFormProps {
  schema: Schema | null | undefined;
  params: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
}

function SchemaForm({ schema, params, onChange }: SchemaFormProps) {
  const { t } = useTranslation();
  if (!schema?.properties) return null;
  const props = schema.properties;
  const required = schema.required || [];

  const inp =
    "w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors font-mono";

  return (
    <div className="space-y-3">
      {Object.entries(props).map(([key, def]) => {
        const isReq = required.includes(key);
        const val = (params[key] as string | number | boolean | undefined) ??
          "";

        if (def.enum) {
          return (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                {key} {isReq && <span className="text-red-400">*</span>}
                <span className="ml-2 text-gray-600 font-normal">
                  {def.description || ""}
                </span>
              </label>
              <Select
                className="w-full"
                value={val as string}
                onChange={(e) => onChange(key, e.target.value)}
              >
                {!isReq && (
                  <option value="">
                    {t("tools.selectPlaceholder", "— vyberte —")}
                  </option>
                )}
                {def.enum.map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </div>
          );
        }

        if (def.type === "boolean") {
          return (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded accent-blue-500"
                checked={!!val}
                onChange={(e) =>
                  onChange(key, e.target.checked)}
              />
              <span className="text-xs text-gray-400">
                {key} {isReq && <span className="text-red-400">*</span>}
                {def.description && (
                  <span className="text-gray-600 ml-1">
                    — {def.description}
                  </span>
                )}
              </span>
            </label>
          );
        }

        if (def.type === "number") {
          return (
            <div key={key}>
              <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                {key} {isReq && <span className="text-red-400">*</span>}
                {def.description && (
                  <span className="ml-2 text-gray-600 font-normal">
                    {def.description}
                  </span>
                )}
              </label>
              <input
                type="number"
                className={inp}
                value={val as number | string}
                onChange={(e) => onChange(key, Number(e.target.value))}
              />
            </div>
          );
        }

        return (
          <div key={key}>
            <label className="block text-xs font-semibold text-gray-400 mb-1.5">
              {key} {isReq && <span className="text-red-400">*</span>}
              {def.description && (
                <span className="ml-2 text-gray-600 font-normal">
                  {def.description}
                </span>
              )}
            </label>
            <input
              className={inp}
              value={val as string}
              onChange={(e) => onChange(key, e.target.value)}
              placeholder={def.default !== undefined ? String(def.default) : ""}
            />
          </div>
        );
      })}
    </div>
  );
}

function ToolRunnerTab({
  tools,
  schemasLoading = false,
}: {
  tools: McpTool[];
  /** true když se ještě dohrávají inputSchema */
  schemasLoading?: boolean;
}) {
  const { t } = useTranslation();
  const [group, setGroup] = useState<string>("");
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [params, setParams] = useState<Record<string, Record<string, unknown>>>(
    {},
  );
  const [result, setResult] = useState<Record<string, unknown> | null>(null);

  // Skupiny = modul (ne všechny tooly v DOM najednou)
  const groups = useMemo(() => {
    const map = new Map<string, McpTool[]>();
    for (const tool of tools) {
      const mod = (tool as McpTool & { module?: string }).module ||
        tool.name.split(/[._]/)[0] ||
        "other";
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod)!.push(tool);
    }
    return [...map.entries()]
      .map(([name, items]) => ({ name, items, count: items.length }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tools]);

  // Výchozí skupina po načtení
  const activeGroup = group && groups.some((g) => g.name === group)
    ? group
    : (groups[0]?.name ?? "");

  const visibleTools = useMemo(() => {
    const list = groups.find((g) => g.name === activeGroup)?.items ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (tool) =>
        tool.name.toLowerCase().includes(q) ||
        (tool.description ?? "").toLowerCase().includes(q),
    );
  }, [groups, activeGroup, filter]);

  const run = useMutation({
    mutationFn: ({ name, p }: { name: string; p: Record<string, unknown> }) =>
      mcpCall<Record<string, unknown>>(name, p),
    onSuccess: (r) => {
      setResult(r);
      toast.success(t("tools.toolCompleted", "Tool dokončen"));
    },
    onError: (e: Error) => {
      toast.error(e.message);
    },
  });

  const toggle = (name: string) => {
    const next = new Set(expanded);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setExpanded(next);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Tools list — po modulech */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Select
            className="sm:w-48"
            value={activeGroup}
            onChange={(e) => {
              setGroup(e.target.value);
              setExpanded(new Set());
            }}
            options={groups.map((g) => ({
              value: g.name,
              label: `${g.name} (${g.count})`,
            }))}
          />
          <input
            className="flex-1 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500"
            placeholder={t("tools.filterTools", "Filtrovat tool v modulu…")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        {schemasLoading && (
          <p className="text-[11px] text-gray-500">
            {t("tools.schemasLoading", "Načítám schémata parametrů…")}
          </p>
        )}
        <p className="text-[11px] text-gray-600">
          {t("tools.groupHint", "{{shown}} toolů ve skupině {{group}} · {{total}} celkem", {
            shown: visibleTools.length,
            group: activeGroup || "—",
            total: tools.length,
          })}
        </p>

        <div className="space-y-2 max-h-[min(70vh,40rem)] overflow-y-auto pr-0.5">
          {visibleTools.map((tool) => {
            const isOpen = expanded.has(tool.name);
            const Icon = GROUP_ICONS[activeGroup] ||
              GROUP_ICONS[tool.name.split(/[._]/)[0] ?? ""] ||
              GROUP_ICONS.default;
            const hasSchema = !!(tool.inputSchema as Schema | undefined)
              ?.properties;

            return (
              <Card
                key={tool.name}
                className={isOpen ? "border-blue-500/30" : ""}
              >
                <button
                  type="button"
                  onClick={() => toggle(tool.name)}
                  className="w-full px-5 py-3 flex items-center gap-3 text-left"
                >
                  <Icon size={16} className="text-blue-400 shrink-0" />
                  <span className="flex-1 text-sm font-medium text-white break-all">
                    {tool.name}
                  </span>
                  {isOpen
                    ? <ChevronDown size={16} className="text-gray-500 shrink-0" />
                    : <ChevronRight size={16} className="text-gray-500 shrink-0" />}
                </button>

                {isOpen && (
                  <CardContent className="border-t border-gray-800">
                    <p className="text-xs text-gray-400 mb-4">
                      {tool.description}
                    </p>

                    {hasSchema
                      ? (
                        <SchemaForm
                          schema={tool.inputSchema as Schema}
                          params={params[tool.name] || {}}
                          onChange={(key, value) =>
                            setParams((prev) => ({
                              ...prev,
                              [tool.name]: { ...prev[tool.name], [key]: value },
                            }))}
                        />
                      )
                      : schemasLoading
                      ? (
                        <p className="text-xs text-gray-500">
                          {t("tools.schemasLoading", "Načítám schémata parametrů…")}
                        </p>
                      )
                      : (
                        <p className="text-xs text-gray-600">
                          {t("tools.noParams", "Bez vstupních parametrů (nebo schéma není k dispozici).")}
                        </p>
                      )}

                    <button
                      type="button"
                      onClick={() =>
                        run.mutate({
                          name: tool.name,
                          p: params[tool.name] || {},
                        })}
                      disabled={run.isPending}
                      className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-semibold text-white transition-colors"
                    >
                      <Play size={14} />
                      {run.isPending
                        ? t("tools.running", "Spouštím...")
                        : t("tools.run", "Spustit")}
                    </button>
                  </CardContent>
                )}
              </Card>
            );
          })}
          {visibleTools.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">
              {t("tools.noToolsInGroup", "V této skupině nic neodpovídá filtru.")}
            </p>
          )}
        </div>
      </div>

      {/* Result */}
      <div>
        <Card className="sticky top-6">
          <CardHeader>
            <CardTitle icon={Terminal}>
              {t("tools.result", "Výsledek")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result
              ? (
                <pre className="acm-terminal text-xs whitespace-pre-wrap max-h-[60vh] overflow-auto p-4 rounded-xl">
                {JSON.stringify(result, null, 2)}
                </pre>
              )
              : (
                <p className="text-sm text-gray-500 text-center py-8">
                  {t(
                    "tools.selectToolHint",
                    "Vyberte tool a spusťte ho pro zobrazení výsledku",
                  )}
                </p>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ToolSchemaTab({ tools }: { tools: CatalogTool[] }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(tools[0]?.name ?? "");
  const current = tools.find((tool) => tool.name === selected) ?? tools[0];

  if (!current) {
    return (
      <p className="text-sm text-gray-500">
        {t("tools.noTools", "Žádné tooly.")}
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle icon={Braces}>{t("tools.schemaTool", "Tool")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Select
            className="w-full"
            value={current.name}
            onChange={(event) => setSelected(event.target.value)}
          >
            {tools.map((tool) => (
              <option key={tool.name} value={tool.name}>{tool.name}</option>
            ))}
          </Select>
          <dl className="mt-4 space-y-2 text-xs">
            <div>
              <dt className="text-gray-600">module</dt>
              <dd className="text-gray-300">{current.module ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-600">scope</dt>
              <dd className="text-gray-300">{current.scope ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-600">resource-qualified</dt>
              <dd className="text-gray-300">
                {current.resource_qualified ? "yes" : "no"}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>{current.name}</CardTitle>
          <CardDescription>{current.description}</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 mb-2">inputSchema</p>
            <pre className="acm-terminal text-xs whitespace-pre-wrap max-h-[55vh] overflow-auto p-4 rounded-xl">
              {JSON.stringify(current.inputSchema ?? {}, null, 2)}
            </pre>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-2">outputSchema</p>
            <pre className="acm-terminal text-xs whitespace-pre-wrap max-h-[55vh] overflow-auto p-4 rounded-xl">
              {JSON.stringify(current.outputSchema ?? {}, null, 2)}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ToolConsumersTab(
  { registrations, observed }: {
    registrations: RegisteredConsumer[];
    observed: RecentToolConsumer[];
  },
) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle icon={Users}>
            {t("tools.registeredConsumers", "Registrovaní konzumenti")}
          </CardTitle>
          <CardDescription>
            {t(
              "tools.registeredConsumersDescription",
              "Aktivní identity a jejich credential profily. Scope není převáděn na domnělý seznam toolů.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {registrations.length === 0
            ? (
              <p className="text-sm text-gray-500">
                {t("tools.noRegisteredConsumers", "Žádní aktivní konzumenti.")}
              </p>
            )
            : (
              <div className="space-y-2 max-h-[65vh] overflow-auto">
                {registrations.map((consumer) => {
                  const capabilities = consumer.proxy === "tool_acl"
                    ? consumer.scopes ?? []
                    : [
                      ...(consumer.allowed_models ?? []).map((value) => `model:${value}`),
                      ...(consumer.allowed_proxies ?? []).map((value) => `proxy:${value}`),
                    ];
                  return (
                    <div
                      key={`${consumer.proxy}:${consumer.credential_id}`}
                      className="p-3 rounded-xl bg-gray-900/40 border border-gray-800/70"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">
                          {consumer.entity_label}
                        </span>
                        <Badge variant={consumer.proxy === "tool_acl" ? "info" : "purple"}>
                          {consumer.proxy}
                        </Badge>
                        <Badge variant="gray">{consumer.entity_type}</Badge>
                        {consumer.identity_class && (
                          <Badge variant="purple">{consumer.identity_class}</Badge>
                        )}
                      </div>
                      {(consumer.purpose || consumer.project_ref) && (
                        <div className="mt-1 text-[10px] text-gray-600 font-mono break-all">
                          {[consumer.purpose, consumer.project_ref].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-400">
                        {consumer.credential_label}
                        {consumer.key_preview && (
                          <code className="ml-2 text-[10px] text-gray-600">
                            {consumer.key_preview}
                          </code>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {capabilities.length
                          ? capabilities.map((capability) => (
                            <Badge key={capability} variant="gray">{capability}</Badge>
                          ))
                          : (
                            <span className="text-[11px] text-gray-600">
                              {t("tools.noDeclaredCapabilities", "bez deklarovaných capability")}
                            </span>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Activity}>
            {t("tools.observedCalls", "Skutečná volání")}
          </CardTitle>
          <CardDescription>
            {t(
              "tools.observedCallsDescription",
              "Auditované použití toolů za posledních 30 dní; nejde o zdroj oprávnění.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {observed.length === 0
            ? (
              <p className="text-sm text-gray-500">
                {t("tools.noObservedCalls", "Žádná auditovaná volání.")}
              </p>
            )
            : (
              <div className="space-y-2 max-h-[65vh] overflow-auto">
                {observed.map((consumer, index) => (
                  <div
                    key={`${consumer.tool}:${consumer.entity_id ?? "unknown"}:${index}`}
                    className="p-3 rounded-xl bg-gray-900/40 border border-gray-800/70"
                  >
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-blue-300 break-all flex-1">
                        {consumer.tool}
                      </code>
                      <Badge variant="info">{consumer.calls}×</Badge>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-gray-500">
                      <span className="truncate">
                        {consumer.entity_label ?? consumer.entity_id ?? t("tools.unknownConsumer", "neznámá identita")}
                        {consumer.entity_type ? ` (${consumer.entity_type})` : ""}
                      </span>
                      <time className="shrink-0">
                        {new Date(consumer.last_call_at).toLocaleString("cs-CZ")}
                      </time>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

interface IdentityEntity {
  id: string;
  label: string;
  type: string;
}

interface EntityDetail {
  api_keys?: Array<
    { id: string; label: string; scopes: string[]; enabled: boolean }
  >;
}

function AclDryRunTab({ tools }: { tools: CatalogTool[] }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [toolName, setToolName] = useState(tools[0]?.name ?? "");
  const [argumentsJson, setArgumentsJson] = useState("{}");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);

  const { data: entityData } = useQuery<{ entities: IdentityEntity[] }>({
    queryKey: ["identity-entities-for-acl"],
    queryFn: () => mcpCall("identity.entity_list"),
  });
  const entities = entityData?.entities ?? [];
  const { data: detail } = useQuery<EntityDetail>({
    queryKey: ["identity-entity-acl-detail", entityId],
    queryFn: () => mcpCall("identity.entity_get", { id: entityId }),
    enabled: !!entityId,
  });
  const credentials = (detail?.api_keys ?? []).filter((key) => key.enabled);

  const check = useMutation({
    mutationFn: async () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(argumentsJson) as Record<string, unknown>;
      } catch {
        throw new Error(
          t(
            "tools.invalidArgumentsJson",
            "Arguments nejsou platný JSON objekt.",
          ),
        );
      }
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
        throw new Error(
          t(
            "tools.invalidArgumentsJson",
            "Arguments nejsou platný JSON objekt.",
          ),
        );
      }
      return await mcpCall<Record<string, unknown>>("identity.acl_check", {
        entity_id: entityId,
        ...(credentialId ? { credential_id: credentialId } : {}),
        tool: toolName,
        arguments: parsed,
      });
    },
    onSuccess: setResult,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle icon={ShieldCheck}>
            {t("tools.aclDryRun", "ACL dry-run")}
          </CardTitle>
          <CardDescription>
            {t(
              "tools.aclDryRunDescription",
              "Vyhodnotí stejnou policy jako MCP proxy. Tool se nespustí.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">entity</label>
            <Select
              className="w-full"
              value={entityId}
              onChange={(event) => {
                setEntityId(event.target.value);
                setCredentialId("");
              }}
            >
              <option value="">—</option>
              {entities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.label} ({entity.type})
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              auth profile
            </label>
            <Select
              className="w-full"
              value={credentialId}
              onChange={(event) => setCredentialId(event.target.value)}
            >
              <option value="">
                {t(
                  "tools.interactiveSessionProfile",
                  "Efektivní interaktivní session",
                )}
              </option>
              {credentials.map((credential) => (
                <option key={credential.id} value={credential.id}>
                  {credential.label} — {credential.scopes.join(", ")}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">tool</label>
            <Select
              className="w-full"
              value={toolName}
              onChange={(event) => setToolName(event.target.value)}
            >
              {tools.map((tool) => (
                <option key={tool.name} value={tool.name}>{tool.name}</option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              arguments
            </label>
            <textarea
              className="w-full min-h-36 bg-gray-900 border border-gray-800 rounded-xl p-3 text-xs font-mono text-gray-200"
              value={argumentsJson}
              onChange={(event) => setArgumentsJson(event.target.value)}
            />
          </div>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-semibold text-white"
            disabled={!entityId || !toolName || check.isPending}
            onClick={() => check.mutate()}
          >
            <ShieldCheck size={14} />
            {check.isPending
              ? t("tools.evaluatingAcl", "Vyhodnocuji…")
              : t("tools.evaluateAcl", "Ověřit oprávnění")}
          </button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{t("tools.aclDecision", "Rozhodnutí")}</CardTitle>
        </CardHeader>
        <CardContent>
          {result
            ? (
              <pre className="acm-terminal text-xs whitespace-pre-wrap max-h-[65vh] overflow-auto p-4 rounded-xl">{JSON.stringify(result, null, 2)}</pre>
            )
            : (
              <p className="text-sm text-gray-500 text-center py-8">
                {t(
                  "tools.aclDecisionHint",
                  "Vyber entitu, tool a resource arguments.",
                )}
              </p>
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Hlavní Komponenta ───────────────────────────────────────────────────

/**
 * Progressive load:
 *  1) light module index (bez schémat) → okamžitý dashboard + seznam po skupinách
 *  2) full schemas na pozadí / při potřebě runneru
 *  3) project tools až na záložce Projekty
 *  4) consumers+recent až na záložce Využití (těžká audit agregace)
 */
export default function Tools() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("modules");

  const { data: modulesData } = useQuery<{ modules: AcmModule[] }>({
    queryKey: ["acm-modules"],
    queryFn: () => mcpCall<{ modules: AcmModule[] }>("acm-self.modules_list"),
    staleTime: 60_000,
  });
  const modules = modulesData?.modules ?? [];

  // 1) Rychlý index — jména/moduly bez inputSchema (~ms místo sekund)
  const lightQ = useQuery<RuntimeToolCatalog>({
    queryKey: ["runtime-tool-catalog", "module-light"],
    queryFn: () =>
      mcpCall<RuntimeToolCatalog>("acm-self.tools_catalog", {
        include: ["module_tools"],
        schemas: false,
      }),
    staleTime: 60_000,
  });

  // 2) Schémata — na pozadí po light load, nebo když uživatel otevře runner/schema/acl
  const needSchemas = activeTab === "modules" || activeTab === "schema" ||
    activeTab === "acl";
  const fullModuleQ = useQuery<RuntimeToolCatalog>({
    queryKey: ["runtime-tool-catalog", "module-full"],
    queryFn: () =>
      mcpCall<RuntimeToolCatalog>("acm-self.tools_catalog", {
        include: ["module_tools"],
        schemas: true,
      }),
    enabled: !!lightQ.data && needSchemas,
    staleTime: 5 * 60_000,
  });

  // 3) Projektové tooly — jen záložka projects / schema / acl
  const needProjects = activeTab === "projects" || activeTab === "schema" ||
    activeTab === "acl";
  const projectQ = useQuery<RuntimeToolCatalog>({
    queryKey: ["runtime-tool-catalog", "projects"],
    queryFn: () =>
      mcpCall<RuntimeToolCatalog>("acm-self.tools_catalog", {
        include: ["project_tools"],
        schemas: true,
      }),
    enabled: needProjects,
    staleTime: 60_000,
  });

  // 4) Využití — těžký audit, jen na vyžádání
  const usageQ = useQuery<RuntimeToolCatalog>({
    queryKey: ["runtime-tool-catalog", "usage"],
    queryFn: () =>
      mcpCall<RuntimeToolCatalog>("acm-self.tools_catalog", {
        include: ["consumers", "recent"],
        recent_days: 14,
        recent_limit: 150,
      }),
    enabled: activeTab === "usage",
    staleTime: 60_000,
  });

  // Prefer full schemas when available, else light index
  const moduleTools: CatalogTool[] = (fullModuleQ.data?.module_tools ??
    lightQ.data?.module_tools ?? []) as CatalogTool[];
  const projectTools: CatalogTool[] = (projectQ.data?.project_tools ??
    []) as CatalogTool[];
  const tools = [...moduleTools, ...projectTools];
  const registeredConsumers = usageQ.data?.registered_consumers ?? [];
  const recentToolConsumers = usageQ.data?.recent_tool_consumers ?? [];
  const schemasLoading = needSchemas && fullModuleQ.isFetching &&
    !fullModuleQ.data;

  if (lightQ.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t("tools.pageTitle", "MCP Tools")}
          description={t("tools.pageDescription", "Nástroje pro správu ACM")}
          icon={Wrench}
        />
        <div className="h-12 w-96 bg-gray-800 rounded-2xl animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-28 bg-gray-800 rounded-2xl animate-pulse"
            />
          ))}
        </div>
        <p className="text-xs text-gray-500">
          {t("tools.loadingIndex", "Načítám katalog toolů…")}
        </p>
      </div>
    );
  }

  if (lightQ.isError) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t("tools.pageTitle", "MCP Tools")}
          icon={Wrench}
        />
        <p className="text-sm text-red-400">
          {(lightQ.error as Error)?.message ??
            t("tools.loadFailed", "Katalog se nepodařilo načíst.")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("tools.pageTitle", "MCP Tools")}
        description={t(
          "tools.pageDescriptionFull",
          "Nástroje pro správu ACM - MCP server",
        )}
        icon={Wrench}
      />

      <Tabs defaultTab="modules" onTabChange={setActiveTab}>
        <TabList>
          <Tab id="modules" icon={<LayoutDashboard size={14} />}>
            {t("tools.tabModuleTools", "Tooly modulů")}
          </Tab>
          <Tab id="projects" icon={<Terminal size={14} />}>
            {t("tools.tabProjectTools", "Tooly projektů")}
          </Tab>
          <Tab id="acl" icon={<ShieldCheck size={14} />}>ACL</Tab>
          <Tab id="schema" icon={<Braces size={14} />}>
            {t("tools.tabSchema", "Schéma")}
          </Tab>
          <Tab id="usage" icon={<Activity size={14} />}>
            {t("tools.tabUsage", "Využití")}
          </Tab>
        </TabList>

        <div className="mt-6">
          <TabPanel id="modules">
            <div className="space-y-8">
              <ToolsDashboard tools={moduleTools} modules={modules} />
              <ToolRunnerTab
                tools={moduleTools}
                schemasLoading={schemasLoading}
              />
            </div>
          </TabPanel>
          <TabPanel id="projects">
            {projectQ.isLoading
              ? (
                <p className="text-sm text-gray-500">
                  {t("tools.loadingProjects", "Načítám projektové tooly…")}
                </p>
              )
              : projectTools.length
              ? <ToolRunnerTab tools={projectTools} />
              : (
                <p className="text-sm text-gray-500">
                  {t(
                    "tools.noProjectTools",
                    "Žádné projektové tooly nejsou v aktuálním resource graphu.",
                  )}
                </p>
              )}
          </TabPanel>
          <TabPanel id="acl">
            {fullModuleQ.isLoading && !fullModuleQ.data
              ? (
                <p className="text-sm text-gray-500">
                  {t("tools.schemasLoading", "Načítám schémata parametrů…")}
                </p>
              )
              : <AclDryRunTab tools={tools} />}
          </TabPanel>
          <TabPanel id="schema">
            {fullModuleQ.isLoading && !fullModuleQ.data
              ? (
                <p className="text-sm text-gray-500">
                  {t("tools.schemasLoading", "Načítám schémata parametrů…")}
                </p>
              )
              : <ToolSchemaTab tools={tools} />}
          </TabPanel>
          <TabPanel id="usage">
            {usageQ.isLoading
              ? (
                <p className="text-sm text-gray-500">
                  {t(
                    "tools.loadingUsage",
                    "Načítám konzumenty a auditované volání…",
                  )}
                </p>
              )
              : usageQ.isError
              ? (
                <p className="text-sm text-red-400">
                  {(usageQ.error as Error)?.message ??
                    t("tools.loadFailed", "Katalog se nepodařilo načíst.")}
                </p>
              )
              : (
                <ToolConsumersTab
                  registrations={registeredConsumers}
                  observed={recentToolConsumers}
                />
              )}
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
